/**
 * Короткий оверлей после полной взятки (4 карты → снапшот очистился) для онлайн open 1×1,
 * в духе reveal-фазы локального {@link ../../belot/hooks/useBelotTrickAnimations}.
 *
 * @module components/online/hooks/useBelotOnlineTrickEndAnimation
 */

import { useEffect, useRef } from 'react';

/**
 * @param {object} args
 * @param {string} args.belotPhase
 * @param {number} args.roundIndex
 * @param {number} args.currentTrickLen
 * @param {object|null|undefined} args.lastCompletedTrick
 * @param {(v: { plays: { seat: number, card: number }[], winnerSeat: number|null, token: number } | null) => void} args.setTrickEndOverlay
 * @param {number} [args.revealMs]
 * @param {(ctx: { trickIndex: number, tricksTakenTotal: number }) => number|undefined} [args.revealMsWhen] — если вернёт число, подменяет revealMs (напр. первая взятка в play дольше для combo).
 * @param {number} [args.tricksTakenTotal] — сумма взяток по командам в текущем снапшоте (после завершения взятки).
 * @param {{ enqueue: function, trickEndPriority?: number }|null} [args.fxQueue] — очередь FX: оверлей конца взятки до combo scoring.
 */
export function useBelotOnlineTrickEndAnimation({
  belotPhase,
  roundIndex,
  currentTrickLen,
  lastCompletedTrick,
  setTrickEndOverlay,
  revealMs = 1050,
  revealMsWhen,
  tricksTakenTotal = 0,
  fxQueue = null,
}) {
  const prevLenRef = useRef(0);
  const lastKeyRef = useRef('');

  useEffect(() => {
    if (belotPhase !== 'play') {
      prevLenRef.current = currentTrickLen;
      return undefined;
    }
    const prev = prevLenRef.current;
    prevLenRef.current = currentTrickLen;
    if (prev !== 4 || currentTrickLen !== 0) return undefined;

    const completed = lastCompletedTrick;
    const plays = completed && Array.isArray(completed.plays) ? completed.plays : null;
    const trickIdx = Number(completed?.trickIndex ?? -1);
    const winnerSeat = Number.isInteger(completed?.winnerSeat) ? completed.winnerSeat : null;
    if (!plays || plays.length !== 4 || trickIdx < 0) return undefined;

    const key = `${Number(roundIndex) || 0}-${trickIdx}`;
    if (key === lastKeyRef.current) return undefined;
    lastKeyRef.current = key;

    let ms = revealMs;
    if (typeof revealMsWhen === 'function') {
      try {
        const alt = revealMsWhen({
          trickIndex: trickIdx,
          tricksTakenTotal: Math.max(0, Number(tricksTakenTotal) || 0),
        });
        if (typeof alt === 'number' && Number.isFinite(alt) && alt > 0) ms = alt;
      } catch {
        /* ignore */
      }
    }

    const playsNorm = plays.map((p) => ({ seat: p.seat, card: p.card }));
    const enq = fxQueue && typeof fxQueue.enqueue === 'function' ? fxQueue.enqueue : null;
    if (enq) {
      const pri = Number(fxQueue.trickEndPriority);
      enq({
        id: `trick-end-overlay-${key}`,
        priority: Number.isFinite(pri) && pri > 0 ? pri : 35,
        minGapMs: 0,
        run: () =>
          new Promise((resolve) => {
            const token = Date.now();
            setTrickEndOverlay({ plays: playsNorm, winnerSeat, token });
            window.setTimeout(() => {
              setTrickEndOverlay((cur) => (cur && cur.token === token ? null : cur));
              resolve();
            }, ms);
          }),
      });
      return undefined;
    }

    const token = Date.now();
    setTrickEndOverlay({ plays: playsNorm, winnerSeat, token });
    const t = window.setTimeout(() => setTrickEndOverlay(null), ms);
    return () => window.clearTimeout(t);
  }, [
    belotPhase,
    roundIndex,
    currentTrickLen,
    lastCompletedTrick,
    setTrickEndOverlay,
    revealMs,
    revealMsWhen,
    tricksTakenTotal,
    fxQueue,
  ]);
}
